import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import {
  useConnections,
  useDashboards,
  useIsAdmin,
  useMeta,
  useReports,
  useSession,
  useThreads,
  type ConnectionSummary,
} from "@/api/queries";
import { AskPanel } from "@/components/home/AskPanel";
import { AttentionStrip, SetupChecklist } from "@/components/home/Attention";
import { FeaturedDashboard } from "@/components/home/FeaturedDashboard";
import { Card, CardBody, CardFooter, CardHeader } from "@/components/ui/Card";
import { StatusDot, type Tone } from "@/components/ui/Chip";
import { ArrowRightIcon, ChatIcon, DashboardIcon, ReportIcon } from "@/components/icons";
import { relativeTime } from "@/lib/time";
import { useBrand } from "@/theme/context";

/**
 * The front page: ask a question, read the key numbers.
 *
 * It was a deployment status page while the platform was being built —
 * design tokens, runtime facts, demo data — which is an operator's reference,
 * not anybody's first screen (those cards live on Operations now). In order:
 *
 * 1. **What needs somebody**, only when something does.
 * 2. **Ask** — a source, a box, and that source's own openers.
 * 3. **Finish setting up**, for an administrator, only while it isn't.
 * 4. **Key numbers** — the dashboard an administrator features. Arranging
 *    that dashboard is how the front page's widgets are placed.
 * 5. **Where you left off** — recent conversations, reports and dashboards,
 *    and the sources there are to ask about.
 *
 * Everything here reads lists an analyst can already read; the one thing the
 * page owns is which dashboard is featured (`GET /api/home`).
 */

function greeting(timeZone: string | undefined): string {
  let hour = new Date().getHours();
  try {
    hour = Number(
      new Intl.DateTimeFormat("en-US", { hour: "numeric", hourCycle: "h23", timeZone }).format(
        new Date(),
      ),
    );
  } catch {
    // An unknown zone falls back to the browser's clock.
  }
  if (hour < 5) return "Good evening";
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

const HEALTH: Record<string, { tone: Tone; label: string }> = {
  ready: { tone: "success", label: "Ready" },
  review: { tone: "warning", label: "Review" },
  attention: { tone: "error", label: "Needs attention" },
};

function ListCard({
  title,
  links,
  empty,
  children,
}: {
  title: string;
  /** Where the whole list lives — at the foot, so a narrow card's title never
   *  has to share its line with them. */
  links: ReactNode;
  empty?: ReactNode;
  children: ReactNode[];
}) {
  return (
    <Card className="flex flex-col">
      <CardHeader title={title} />
      <CardBody className="flex-1 pt-0">
        {children.length ? (
          <ul className="divide-border -mx-2 flex flex-col divide-y">{children}</ul>
        ) : (
          <p className="type-support text-ink-secondary">{empty}</p>
        )}
      </CardBody>
      <CardFooter>
        <div className="flex flex-wrap gap-x-4 gap-y-1">{links}</div>
      </CardFooter>
    </Card>
  );
}

function Row({
  icon,
  title,
  meta,
  to,
}: {
  icon: ReactNode;
  title: string;
  meta: string;
  to: ReactNode;
}) {
  return (
    <li className="hover:bg-hover rounded-element motion-fast relative flex items-center gap-3 px-2 py-2.5 transition-colors">
      <span className="text-ink-disabled shrink-0">{icon}</span>
      <div className="min-w-0 flex-1">
        <p className="type-label text-ink truncate">{title}</p>
        <p className="type-support text-ink-secondary truncate">{meta}</p>
      </div>
      {/* The link covers the row, so the whole row is the target. */}
      {to}
    </li>
  );
}

const COVER = "absolute inset-0 rounded-element focus-visible:outline-2 focus-visible:outline-accent";

function SeeAll({ to, label }: { to: "/chat" | "/reports" | "/dashboards" | "/sources"; label: string }) {
  return (
    <Link to={to} className="type-support text-accent inline-flex items-center gap-1 hover:underline">
      {label}
      <ArrowRightIcon className="size-3.5" />
    </Link>
  );
}

function SourceRow({ source }: { source: ConnectionSummary }) {
  const health = HEALTH[source.health_verdict];
  return (
    <li className="flex items-center gap-3 px-2 py-2.5">
      <StatusDot tone={health?.tone ?? "neutral"} />
      <div className="min-w-0 flex-1">
        <Link
          to="/sources/$connectionId"
          params={{ connectionId: source.id }}
          className="type-label text-ink block truncate hover:underline"
        >
          {source.name}
        </Link>
        <p className="type-support text-ink-secondary truncate">
          {health ? health.label : "Not checked"}
          {source.has_data_dictionary || source.has_native_comments ? "" : " · undocumented"}
        </p>
      </div>
    </li>
  );
}

export function HomePage() {
  const { productName, welcomeTitle, welcomeBody } = useBrand();
  const { data: meta } = useMeta();
  const { data: session } = useSession();
  const isAdmin = useIsAdmin();
  const { data: threads } = useThreads("");
  const { data: reports } = useReports();
  const { data: dashboards } = useDashboards();
  const { data: connections } = useConnections();
  const locale = meta?.locale ?? "en-US";

  const recent = (threads ?? []).filter((thread) => thread.message_count > 0).slice(0, 6);
  // Reports and dashboards together, most recently touched first: "the thing I
  // was looking at" is one list to the person looking, not two.
  const saved = [
    ...(reports ?? []).map((report) => ({
      kind: "report" as const,
      id: report.id,
      title: report.title || "Untitled report",
      when: report.last_refreshed_at ?? report.updated_at,
      meta: `${report.tile_count} tile${report.tile_count === 1 ? "" : "s"}${report.connection_name ? ` · ${report.connection_name}` : ""}`,
    })),
    ...(dashboards ?? []).map((dashboard) => ({
      kind: "dashboard" as const,
      id: dashboard.id,
      title: dashboard.name || "Untitled dashboard",
      when: dashboard.last_refreshed_at ?? dashboard.updated_at,
      meta: `${dashboard.tile_count} tile${dashboard.tile_count === 1 ? "" : "s"}`,
    })),
  ]
    .sort((left, right) => (right.when ?? "").localeCompare(left.when ?? ""))
    .slice(0, 6);
  const sources = connections ?? [];
  const who = session?.user?.display_name || session?.user?.username;

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 py-8 sm:px-8 sm:py-10">
      <header>
        <p className="type-caption text-ink-disabled">
          {greeting(meta?.timezone)}
          {who ? `, ${who}` : ""}
        </p>
        <h1 className="type-h1 text-ink mt-2 text-balance">
          {welcomeTitle || `What would you like to know?`}
        </h1>
        {/* Client copy when there is any, the platform's sentence when there is
            not — an empty field means "say the usual thing", never "say nothing"
            (PRD FR-11.1). */}
        <p className="type-body text-ink-secondary mt-2 max-w-2xl">
          {welcomeBody ||
            `Ask ${productName} about your data in plain English. Every answer carries the read-only SQL that produced it, and any of them can become a report or a dashboard tile.`}
        </p>
      </header>

      <AttentionStrip />
      <AskPanel />
      {isAdmin ? <SetupChecklist /> : null}
      <FeaturedDashboard />

      {/* Container queries, not viewport breakpoints: the sidebar folds, so the
          window's width says little about the width this grid actually has. */}
      <div className="@container">
      <div className="grid gap-5 @2xl:grid-cols-2 @5xl:grid-cols-3">
        <ListCard
          title="Recent conversations"
          links={<SeeAll to="/chat" label="All conversations" />}
          empty="Nothing asked yet. Ask above and the conversation appears here."
        >
          {recent.map((thread) => (
            <Row
              key={thread.id}
              icon={<ChatIcon className="size-4" />}
              title={thread.title || "Untitled conversation"}
              meta={[
                (thread.connection_names?.length ? thread.connection_names : [thread.connection_name])
                  .filter(Boolean)
                  .join(" + "),
                relativeTime(thread.last_message_at ?? thread.updated_at, locale),
              ]
                .filter(Boolean)
                .join(" · ")}
              to={
                <Link
                  to="/chat/$threadId"
                  params={{ threadId: thread.id }}
                  aria-label={thread.title || "Untitled conversation"}
                  className={COVER}
                />
              }
            />
          ))}
        </ListCard>

        <ListCard
          title="Reports and dashboards"
          links={
            <>
              <SeeAll to="/reports" label="Reports" />
              <SeeAll to="/dashboards" label="Dashboards" />
            </>
          }
          empty="Pin an answer as a report, or add it to a dashboard, and it appears here."
        >
          {saved.map((item) => (
            <Row
              key={`${item.kind}-${item.id}`}
              icon={
                item.kind === "report" ? (
                  <ReportIcon className="size-4" />
                ) : (
                  <DashboardIcon className="size-4" />
                )
              }
              title={item.title}
              meta={[item.meta, item.when ? `updated ${relativeTime(item.when, locale)}` : ""]
                .filter(Boolean)
                .join(" · ")}
              to={
                item.kind === "report" ? (
                  <Link
                    to="/reports/$reportId"
                    params={{ reportId: item.id }}
                    aria-label={item.title}
                    className={COVER}
                  />
                ) : (
                  <Link
                    to="/dashboards/$dashboardId"
                    params={{ dashboardId: item.id }}
                    aria-label={item.title}
                    className={COVER}
                  />
                )
              }
            />
          ))}
        </ListCard>

        <ListCard
          title="Sources"
          links={<SeeAll to="/sources" label={isAdmin ? "Manage sources" : "All sources"} />}
          empty="No sources connected yet."
        >
          {sources.slice(0, 8).map((source) => (
            <SourceRow key={source.id} source={source} />
          ))}
        </ListCard>
      </div>
      </div>
    </div>
  );
}
